import { useEffect, useState } from 'react'
import axios from 'axios'
import type { BoxTruckMyDriverCompanyDepositDto } from '@numeo/types-core'
import { getUserErrorMessage } from '@/utils/get-user-error-message'
import { fetchMyDriverCompanyDeposit } from '../services/myDriverCompanyDepositApi'
import { trackBoxTruckUiInteraction } from '../utils/analytics'

type ReadState =
    | { userId: string; status: 'loading' }
    | { userId: string; status: 'ready'; values: BoxTruckMyDriverCompanyDepositDto }
    | { userId: string; status: 'error'; message: string }

const failureReason = (error: unknown): 'access' | 'not_found' | 'server' | 'network' | 'other' => {
    if (!axios.isAxiosError(error)) return 'other'
    const status = error.response?.status
    if (status === 401 || status === 403) return 'access'
    if (status === 404) return 'not_found'
    if (status && status >= 500) return 'server'
    return status ? 'other' : 'network'
}

/** Key the answer by driver so switching rows cannot paint the previous driver's financial details. */
export const useMyDriverCompanyDeposit = (userId: string) => {
    const [state, setState] = useState<ReadState>({ userId, status: 'loading' })

    useEffect(() => {
        const controller = new AbortController()
        setState({ userId, status: 'loading' })
        fetchMyDriverCompanyDeposit(userId, controller.signal)
            .then((values) => {
                if (!controller.signal.aborted) {
                    setState({ userId, status: 'ready', values })
                    trackBoxTruckUiInteraction('driver_financial_details_read', undefined, { outcome: 'loaded' })
                }
            })
            .catch((error: unknown) => {
                if (!controller.signal.aborted) {
                    setState({ userId, status: 'error', message: getUserErrorMessage(error) || 'Could not load company and direct deposit details.' })
                    trackBoxTruckUiInteraction('driver_financial_details_read', undefined, { outcome: 'failed', reason: failureReason(error) })
                }
            })
        return () => controller.abort()
    }, [userId])

    const current: ReadState = state.userId === userId ? state : { userId, status: 'loading' }
    return current
}
